import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';

const FeedbackContext = createContext();
export const useFeedback = () => useContext(FeedbackContext);

export const FeedbackProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [modalConfig, setModalConfig] = useState(null);
  const [stagedMessageConfig, setStagedMessageConfig] = useState(null);
  const [syncProgressConfig, setSyncProgressConfig] = useState(null);
  const [largePdfConfig, setLargePdfConfig] = useState(null);
  const largePdfResolverRef = useRef(null);

  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const showConfirm = useCallback((config) => {
    setModalConfig(config);
  }, []);

  const closeConfirm = useCallback(() => {
    setModalConfig(null);
  }, []);

  const showStagedMessages = useCallback((stages, action) => {
    let currentStage = 0;
    setStagedMessageConfig({ stages, currentStage, isComplete: false });

    const runAction = async () => {
      try {
        if (typeof action === 'function') {
          await action();
        }
        setStagedMessageConfig(prev => prev ? { ...prev, isComplete: true, currentStage: stages.length - 1 } : null);
        setTimeout(() => setStagedMessageConfig(null), 800);
      } catch (error) {
        setStagedMessageConfig(null);
        throw error;
      }
    };

    const intervalId = setInterval(() => {
      currentStage++;
      if (currentStage < stages.length) {
        setStagedMessageConfig(prev => prev ? { ...prev, currentStage } : null);
      } else {
        clearInterval(intervalId);
      }
    }, 600);

    runAction().finally(() => clearInterval(intervalId));
  }, []);

  const showSyncProgress = useCallback((config) => {
    const { action } = config;
    const title = 'Preparing your workspace';
    const stages = [
      'Checking workspace availability',
      'Loading portfolio records',
      'Updating workspace data',
      'Refreshing visible pages',
      'Confirming readiness'
    ];
    const successMessage = 'Workspace is up to date.';
    const errorMessage = 'Workspace update could not be completed.';

    return new Promise((resolve, reject) => {
      let currentStage = 0;
      let isComplete = false;
      let isError = false;
      let errorText = '';
      let actionResult = null;

      const updateState = () => {
        setSyncProgressConfig({
          title,
          stages,
          currentStage,
          isComplete,
          isError,
          errorText,
          successMessage,
          errorMessage,
          canClose: isComplete || isError
        });
      };

      updateState();

      const advanceStage = () => {
        if (!isComplete && !isError && currentStage < stages.length - 1) {
          currentStage++;
          updateState();
        }
      };

      const stageInterval = setInterval(advanceStage, 700);

      const runAction = async () => {
        try {
          actionResult = await action();
          clearInterval(stageInterval);

          if (actionResult?.success === false) {
            isError = true;
            errorText = 'We could not complete the workspace update. Please try again.';
            updateState();
            resolve({ ...actionResult, error: errorText });
            return;
          }

          currentStage = stages.length - 1;
          isComplete = true;
          updateState();

          setTimeout(() => {
            setSyncProgressConfig(null);
            resolve(actionResult);
          }, 1200);
        } catch (error) {
          clearInterval(stageInterval);
          isError = true;
          errorText = 'We could not complete the workspace update. Please try again.';
          updateState();
          // Don't auto-close on error - let user close manually
          // Resolve with generic error info so caller can handle it without exposing infrastructure details
          resolve({ success: false, error: errorText, _isSyncError: true });
        }
      };

      runAction();
    });
  }, []);

  const closeSyncProgress = useCallback(() => {
    setSyncProgressConfig(null);
  }, []);

  const resolveLargePdfDecision = useCallback((decision) => {
    const resolver = largePdfResolverRef.current;
    largePdfResolverRef.current = null;
    setLargePdfConfig(null);
    if (resolver) resolver(decision);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const requestLargePdfDecision = (config = {}) => new Promise((resolve) => {
      if (largePdfResolverRef.current) {
        largePdfResolverRef.current('replace');
      }
      largePdfResolverRef.current = resolve;
      setLargePdfConfig(config);
    });

    window.__mppRequestLargePdfDecision = requestLargePdfDecision;

    return () => {
      if (window.__mppRequestLargePdfDecision === requestLargePdfDecision) {
        delete window.__mppRequestLargePdfDecision;
      }
      if (largePdfResolverRef.current) {
        largePdfResolverRef.current('replace');
        largePdfResolverRef.current = null;
      }
    };
  }, []);

  return (
    <FeedbackContext.Provider value={{ showToast, showConfirm, showStagedMessages, showSyncProgress }}>
      {children}

      {/* Toasts */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-fade-in-up pointer-events-auto ${toast.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : toast.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-slate-800 text-white border border-slate-700'}`}
          >
            <SafeIcon icon={toast.type === 'success' ? 'CheckCircle' : toast.type === 'error' ? 'AlertCircle' : 'Info'} className="text-lg" />
            {toast.message}
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      {modalConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
            <div className="p-6">
              <h3 className="text-xl font-semibold text-slate-800 mb-2">{modalConfig.title}</h3>
              <p className="text-slate-600 mb-6">{modalConfig.message}</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeConfirm}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  {modalConfig.cancelText || 'Cancel'}
                </button>
                <button
                  onClick={() => {
                    if (modalConfig.onConfirm) modalConfig.onConfirm();
                    closeConfirm();
                  }}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${modalConfig.isDestructive ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-800 hover:bg-slate-900'}`}
                >
                  {modalConfig.confirmText || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Large PDF Upload Decision */}
      {largePdfConfig && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/55 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl animate-scale-in">
            <div className="p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-200">
                  <SafeIcon icon="FileText" className="text-xl" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-slate-900">Large PDF detected</h3>
                  <p className="mt-1 break-words text-sm font-medium text-slate-700">{largePdfConfig.fileName || 'PDF document'}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    This PDF is <span className="font-semibold text-slate-800">{largePdfConfig.sizeLabel || 'over 2 MB'}</span>, above the recommended 2 MB size.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-900">
                For faster uploads and document previews, you can stop now and choose a smaller/compressed copy, or continue with the original file. MPP will not alter the PDF automatically.
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => resolveLargePdfDecision('replace')}
                  className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  Use Smaller PDF
                </button>
                <button
                  type="button"
                  onClick={() => resolveLargePdfDecision('proceed')}
                  className="min-h-[44px] rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  Upload Original
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Staged Messages Modal */}
      {stagedMessageConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
                  <SafeIcon icon="Loader" className={`text-xl ${stagedMessageConfig.isComplete ? '' : 'animate-spin'}`} />
                </div>
                <h3 className="text-lg font-semibold text-slate-800">
                  {stagedMessageConfig.isComplete ? 'Complete!' : 'Processing...'}
                </h3>
              </div>
              <div className="space-y-2">
                {stagedMessageConfig.stages.map((stage, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 text-sm ${index < stagedMessageConfig.currentStage ? 'text-emerald-600' : index === stagedMessageConfig.currentStage ? 'text-amber-600 font-medium' : 'text-slate-400'}`}
                  >
                    <SafeIcon
                      icon={index < stagedMessageConfig.currentStage ? 'CheckCircle' : index === stagedMessageConfig.currentStage ? 'Circle' : 'Circle'}
                      className="text-lg"
                    />
                    {stage}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Progress Modal */}
      {syncProgressConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-in">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${syncProgressConfig.isError ? 'bg-red-100 text-red-600' : syncProgressConfig.isComplete ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                  <SafeIcon
                    icon={syncProgressConfig.isError ? 'AlertCircle' : syncProgressConfig.isComplete ? 'CheckCircle' : 'Loader'}
                    className={`text-xl ${!syncProgressConfig.isComplete && !syncProgressConfig.isError ? 'animate-spin' : ''}`}
                  />
                </div>
                <h3 className="text-lg font-semibold text-slate-800">
                  {syncProgressConfig.isError ? 'Update Failed' : syncProgressConfig.isComplete ? 'Workspace Ready' : syncProgressConfig.title}
                </h3>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${syncProgressConfig.isError ? 'bg-red-500' : syncProgressConfig.isComplete ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    style={{ width: `${Math.min(((syncProgressConfig.currentStage + 1) / syncProgressConfig.stages.length) * 100, 100)}%` }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {syncProgressConfig.isError ? 'Update interrupted' : syncProgressConfig.isComplete ? '100%' : `${Math.round(((syncProgressConfig.currentStage + 1) / syncProgressConfig.stages.length) * 100)}%`}
                </div>
              </div>

              {/* Staged Messages */}
              <div className="space-y-2">
                {syncProgressConfig.stages.map((stage, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 text-sm ${index < syncProgressConfig.currentStage ? 'text-emerald-600' : index === syncProgressConfig.currentStage && !syncProgressConfig.isComplete && !syncProgressConfig.isError ? 'text-amber-600 font-medium' : syncProgressConfig.isComplete ? 'text-emerald-600' : 'text-slate-400'}`}
                  >
                    <SafeIcon
                      icon={index <= syncProgressConfig.currentStage || syncProgressConfig.isComplete ? 'CheckCircle' : index === syncProgressConfig.currentStage && syncProgressConfig.isError ? 'AlertCircle' : 'Circle'}
                      className="text-lg"
                    />
                    {stage}
                  </div>
                ))}
              </div>

              {/* Success/Error Message */}
              {(syncProgressConfig.isComplete || syncProgressConfig.isError) && (
                <div className={`mt-4 p-3 rounded-lg text-sm font-medium ${syncProgressConfig.isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                  {syncProgressConfig.isError ? syncProgressConfig.errorText : syncProgressConfig.successMessage}
                </div>
              )}

              {/* Close Button - only when complete or error */}
              {syncProgressConfig.canClose && (
                <div className="flex justify-end mt-4">
                  <button
                    onClick={closeSyncProgress}
                    className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </FeedbackContext.Provider>
  );
};